import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, formatDateRange } from '@utils/dateUtils';
import type { IInvestigationReport } from '@apis/investigationReports';
import { RecentCardShell } from '../RecentReports/RecentCardShell';
import shellStyles from '../RecentReports/RecentCardShell.module.css';

interface IWorkerTimesheetRecentCardProps {
  submission: IInvestigationReport;
  siteId: string;
}

/**
 * Recent-rail rendering for a Worker Timesheet submission. Renders
 * `[date range] · [N workers]` in the meta-row middle slot.
 *
 * Why not show names: in practice the rail accumulates several reports
 * with the same crew across different windows, so the names don't
 * differentiate rows — the date range does. Names also tended to leak
 * the upstream tag id format ("32_simulation-eng") when the BFF's tag
 * roster carries no human label, which violates the FE invariant that
 * source-of-truth is internal (`frontend/CLAUDE.md`). Worker-level
 * detail belongs on the report page, not the rail.
 */
export function WorkerTimesheetRecentCard({ submission, siteId }: IWorkerTimesheetRecentCardProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ja' ? 'ja' : 'en';
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;

  // The window is the whole subject of this report. When a legacy record
  // carries no `dateFrom`/`dateTo`, show no date rather than substituting the
  // archive instant: that would put the moment the timesheet was *generated*
  // where the range it *covers* belongs. The creation time is already in
  // `RecentCardShell`'s top-right slot, labelled as such.
  const rangeText =
    submission.dateFrom && submission.dateTo
      ? formatDateRange(submission.dateFrom, submission.dateTo, lang, tz)
      : '';

  const workerCount = submission.tagIds?.length ?? 0;
  // i18next picks `workerCount_one` / `workerCount_other` for en automatically
  // off the `count` interpolation; ja resolves to the single `workerCount`
  // key since Japanese has no grammatical plurals.
  const workerCountText =
    workerCount > 0
      ? t('analytics.recent.workersTimesheet.workerCount', { count: workerCount })
      : '';

  // Title fallback — legacy / mock records may submit an empty title; the
  // i18n key keeps the rail row identifiable.
  const title =
    submission.title && submission.title.length > 0
      ? submission.title
      : t('reports.workersTimesheet.title');

  return (
    <RecentCardShell
      reportType="workersTimesheet"
      reportId={submission.reportId}
      siteId={siteId}
      createdAtIso={submission.dateGeneratedIso}
      title={title}
    >
      {rangeText && <span className={shellStyles.subtitle}>{rangeText}</span>}
      {workerCountText && (
        <>
          {rangeText && <span className={shellStyles.divider} aria-hidden="true" />}
          <span className={shellStyles.subtitle}>{workerCountText}</span>
        </>
      )}
    </RecentCardShell>
  );
}
